import { createContext, useContext, useState, useCallback } from 'react';
import { syncProvider as apiSyncProvider, syncAllProviders as apiSyncAllProviders } from '../lib/api';

const SyncContext = createContext(null);

export const SyncProvider = ({ children }) => {
  // Set of sync keys currently running: provider.id or 'all'
  const [syncingKeys, setSyncingKeys] = useState(new Set());

  const isSyncing = syncingKeys.size > 0;

  const _add = (key) => setSyncingKeys(prev => new Set([...prev, key]));
  const _remove = (key) => setSyncingKeys(prev => { const n = new Set(prev); n.delete(key); return n; });

  const syncOne = useCallback(async (provider) => {
    const key = provider.id;
    _add(key);
    try {
      const response = await apiSyncProvider(provider.id);
      return { ok: true, records: response.data?.records_imported || 0 };
    } catch (error) {
      return { ok: false, error: error.response?.data?.detail || 'Erreur de synchronisation' };
    } finally {
      _remove(key);
    }
  }, []);

  const syncAll = useCallback(async () => {
    _add('all');
    try {
      const response = await apiSyncAllProviders();
      return { ok: true, results: response.data?.results || [] };
    } catch (error) {
      return { ok: false, error: error.response?.data?.detail || 'Erreur de synchronisation' };
    } finally {
      _remove('all');
    }
  }, []);

  const isSyncingProvider = useCallback((id) => syncingKeys.has(id), [syncingKeys]);

  return (
    <SyncContext.Provider value={{ isSyncing, syncingKeys, syncOne, syncAll, isSyncingProvider }}>
      {children}
    </SyncContext.Provider>
  );
};

export const useSync = () => {
  const ctx = useContext(SyncContext);
  if (!ctx) throw new Error('useSync must be used within SyncProvider');
  return ctx;
};
